/**
 * brushHistory.js — отмена и повтор МАЗКОВ КИСТИ. [12.0.54]
 *
 * ПОВОД. Кнопки «↩ Отмена» и «↪ Повтор» во вкладке «Сегментация» звали
 * отмену ЗАМЕРОВ (ui/toolBindings.js undoAnnotation) — та хранит линейки и
 * углы, мазков в ней нет. На стенде с настоящим движком: два мазка кистью,
 * «Отмена» → «Нечего отменять», маска та же. Мазки при этом запоминал сам
 * Cornerstone: каждый мазок кладёт в общую историю движка
 * (core utilities.HistoryMemo.DefaultHistoryMemo) запись с
 * operationType: 'labelmap' — снимок вокселей до и после.
 *
 * ПОЧЕМУ СВОЯ СТОПКА, А НЕ DefaultHistoryMemo.undo(). История движка общая:
 * туда же ложатся замеры. Её undo() снял бы последнее ЛЮБОЕ действие — врач
 * жмёт «Отмена» в «Сегментации», а пропадает линейка в другом окне. А
 * undoIf(«только мазок») молча отказывает, если поверх мазка лёг хоть один
 * замер, — и отмена мазков встаёт навсегда. Поэтому мазки копятся здесь, по
 * одному на запись движка, и откатываются её же restoreMemo — тем кодом
 * Cornerstone, что их и записал.
 *
 * Ноль импортов: проверяется в node на подменных записях (tests/segtab_test.mjs).
 * EXPORTS: создатьИсториюМазков, ПРЕДЕЛ_МАЗКОВ
 */

/** Сколько мазков помнить. 50 — размер истории самого Cornerstone
 *  (HistoryMemo, size = 50 по умолчанию); глубже движок тоже не держит. */
export const ПРЕДЕЛ_МАЗКОВ = 50;

const вМассив = (з) => (Array.isArray(з) ? з : [з]);

/** Запись относится к маске? Группа записей — если хоть одна из них о маске. */
export function этоМазок(запись) {
  return вМассив(запись).some((з) => з && з.operationType === 'labelmap' && typeof з.restoreMemo === 'function');
}

/** Чья маска в записи (segmentationId). У группы — первой записи с полем. */
function маскаЗаписи(запись) {
  for (const з of вМассив(запись)) if (з?.segmentationId) return String(з.segmentationId);
  return null;
}

function откатить(запись, отмена) {
  const части = вМассив(запись).filter((з) => з && typeof з.restoreMemo === 'function');
  // Группа отменяется с конца, повторяется с начала — как в HistoryMemo движка.
  for (const з of (отмена ? части.slice().reverse() : части)) з.restoreMemo(отмена);
}

/**
 * @param {number} [предел]
 * @returns {{запомнить:Function, отменить:Function, повторить:Function, сколько:Function, забыть:Function}}
 */
export function создатьИсториюМазков(предел = ПРЕДЕЛ_МАЗКОВ) {
  const п = Math.max(1, Math.trunc(Number(предел) || ПРЕДЕЛ_МАЗКОВ));
  let отмены = [];
  let повторы = [];

  /** Убрать с верха стопки записи ЧУЖОЙ маски (маска пересоздана под другую серию). */
  const снятьЧужие = (стопка, маска) => {
    let снято = 0;
    while (стопка.length && маска && маскаЗаписи(стопка[стопка.length - 1]) && маскаЗаписи(стопка[стопка.length - 1]) !== String(маска)) {
      стопка.pop(); снято++;
    }
    return снято;
  };

  return {
    /** Новый мазок. Повторы сбрасываются — как в любой истории правок. */
    запомнить(запись) {
      if (!этоМазок(запись)) return false;
      отмены.push(запись);
      if (отмены.length > п) отмены.splice(0, отмены.length - п);
      повторы = [];
      return true;
    },
    /**
     * Отменить последний мазок.
     * @param {string|null} маска — segmentationId действующей маски
     * @returns {{ок:true, осталось:number, повторов:number}|{ок:false, причина:string}}
     */
    отменить(маска = null) {
      const снято = снятьЧужие(отмены, маска);
      const з = отмены.pop();
      if (!з) {
        return { ок: false, причина: снято
          ? 'Мазки прежней маски (до смены серии) отменить нельзя — их маски уже нет'
          : 'Мазков кистью для отмены нет' };
      }
      try { откатить(з, true); } catch (e) { отмены.push(з); return { ок: false, причина: 'Отмена мазка не удалась: ' + e.message }; }
      повторы.push(з);
      return { ок: true, осталось: отмены.length, повторов: повторы.length };
    },
    /** Вернуть отменённый мазок. */
    повторить(маска = null) {
      снятьЧужие(повторы, маска);
      const з = повторы.pop();
      if (!з) return { ок: false, причина: 'Повторять нечего: отменённых мазков нет' };
      try { откатить(з, false); } catch (e) { повторы.push(з); return { ок: false, причина: 'Повтор мазка не удался: ' + e.message }; }
      отмены.push(з);
      return { ок: true, осталось: отмены.length, повторов: повторы.length };
    },
    сколько() { return { отмен: отмены.length, повторов: повторы.length }; },
    /** Забыть всё — после «Очистить»: наложить старый мазок на стёртую маску значило бы вернуть обрывки. */
    забыть() { const н = отмены.length + повторы.length; отмены = []; повторы = []; return н; },
  };
}

export default { создатьИсториюМазков, этоМазок, ПРЕДЕЛ_МАЗКОВ };
